import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import {
  leadApi,
  type LeadsCsvExportSourcePage,
  type LeadsFilterPayload,
  type StartLeadsCsvExportPayload
} from "api/lead";
import type { ColumnVisibilityItem } from "providers/Auth";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import type { LeadsCsvDownloadState } from "shared/app/LeadsTableView/components/LeadsHeader";
import { LeadColumnId } from "shared/app/LeadsTableView/constants";
import {
  getLeadColumnHeader,
  getVisibleLeadColumnIds
} from "shared/app/LeadsTableView/utils/leadColumns";

const EXPORT_FAILED_MESSAGE = "Could not build the export. Please try again.";
const STATUS_POLL_INTERVAL_MS = 3000;
const MAX_STATUS_RETRIES = 3;
const MAX_EXPORT_WAIT_MS = 30 * 60 * 1000;
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const getSourcePage = (
  propertyIds?: string[],
  unitIds?: string[]
): LeadsCsvExportSourcePage => {
  if (unitIds?.length) return "unit";
  if (propertyIds?.length) return "property";
  return "leads";
};

const getDownloadState = ({
  isExporting,
  isFetching,
  totalCount
}: {
  isExporting: boolean;
  isFetching: boolean;
  totalCount?: number;
}): LeadsCsvDownloadState => {
  if (isExporting) return "exporting";
  if (isFetching || totalCount === undefined) return "loading";
  if (totalCount === 0) return "empty";
  return "ready";
};

const shouldRetryStatus = (failureCount: number, error: unknown) =>
  !(axios.isAxiosError(error) && error.response?.status === 404) &&
  failureCount < MAX_STATUS_RETRIES;

const useCsvExportJob = () => {
  const [exportRun, setExportRun] = useState<{
    id: string;
    startedAt: number;
  } | null>(null);
  const exportId = exportRun?.id ?? null;

  const startExport = useMutation({
    mutationFn: (payload: StartLeadsCsvExportPayload) =>
      leadApi.startCsvExport(payload),
    onSuccess: (data) => setExportRun({ id: data.id, startedAt: Date.now() }),
    onError: () => toast.error(EXPORT_FAILED_MESSAGE),
    mutationKey: ["LEADS_CSV_EXPORT", "CREATE"]
  });

  const {
    data: exportStatus,
    dataUpdatedAt,
    isError: statusFailed
  } = useQuery({
    queryKey: ["LEADS_CSV_EXPORT_STATUS", exportId],
    queryFn: () => leadApi.getCsvExportStatus(exportId as string),
    enabled: Boolean(exportId),
    retry: shouldRetryStatus,
    refetchInterval: (query) =>
      query.state.data?.status === "pending" ? STATUS_POLL_INTERVAL_MS : false
  });

  useEffect(() => {
    if (!statusFailed) return;

    toast.error(EXPORT_FAILED_MESSAGE);
    setExportRun(null);
  }, [statusFailed]);

  useEffect(() => {
    if (!exportRun || exportStatus?.status !== "pending") return;
    if (dataUpdatedAt - exportRun.startedAt <= MAX_EXPORT_WAIT_MS) return;

    toast.error(EXPORT_FAILED_MESSAGE);
    setExportRun(null);
  }, [exportRun, exportStatus, dataUpdatedAt]);

  useEffect(() => {
    if (!exportStatus) return;

    if (exportStatus.status === "done" && exportStatus.download_url) {
      window.location.href = exportStatus.download_url;
      setExportRun(null);
      return;
    }

    if (exportStatus.status === "failed") {
      toast.error(EXPORT_FAILED_MESSAGE);
      setExportRun(null);
    }
  }, [exportStatus]);

  return {
    startExport: startExport.mutate,
    isExporting: startExport.isPending || Boolean(exportId)
  };
};

export const useLeadsCsvExport = ({
  canDownload,
  isNeedsReview,
  filters,
  orderBy,
  orderDirection,
  columnsVisibility,
  isLeadTypeEnabled,
  isLeadScoringEnabled,
  hidePropertyInterested,
  propertyIds,
  unitIds,
  isFetching,
  totalCount
}: {
  canDownload: boolean;
  isNeedsReview: boolean;
  filters: LeadsFilterPayload;
  orderBy?: string;
  orderDirection?: "asc" | "desc";
  columnsVisibility: ColumnVisibilityItem[];
  isLeadTypeEnabled: boolean;
  isLeadScoringEnabled: boolean;
  hidePropertyInterested: boolean;
  propertyIds?: string[];
  unitIds?: string[];
  isFetching: boolean;
  totalCount?: number;
}): { state: LeadsCsvDownloadState; onDownload: () => void } | undefined => {
  const { Property: propertyLabel, Unit: unitLabel } =
    useUnitsPropertiesDictionary();
  const { startExport, isExporting } = useCsvExportJob();

  const columns = useMemo(
    () =>
      getVisibleLeadColumnIds({
        columnsVisibility,
        isLeadTypeEnabled,
        isLeadScoringEnabled,
        hidePropertyInterested
      })
        .filter((columnId) => columnId !== LeadColumnId.Name)
        .map((columnId) => ({
          id: columnId,
          label: getLeadColumnHeader(columnId, { propertyLabel, unitLabel })
        })),
    [
      columnsVisibility,
      isLeadTypeEnabled,
      isLeadScoringEnabled,
      hidePropertyInterested,
      propertyLabel,
      unitLabel
    ]
  );

  const onDownload = useCallback(() => {
    startExport({
      filters,
      order_by: orderBy,
      order_direction: orderDirection,
      columns,
      timezone: BROWSER_TIMEZONE,
      source_page: getSourcePage(propertyIds, unitIds),
      property_id: propertyIds?.[0],
      unit_id: unitIds?.[0]
    });
  }, [
    startExport,
    filters,
    orderBy,
    orderDirection,
    columns,
    propertyIds,
    unitIds
  ]);

  if (!canDownload || isNeedsReview) return undefined;

  return {
    state: getDownloadState({ isExporting, isFetching, totalCount }),
    onDownload
  };
};
